/**
 * Column Editor
 *
 * Individual column row with inline editing for the table editor.
 * Supports drag-and-drop reordering via @hello-pangea/dnd.
 */

import React, { useState } from "react";
import Box from "@mui/joy/Box";
import Input from "@mui/joy/Input";
import Checkbox from "@mui/joy/Checkbox";
import IconButton from "@mui/joy/IconButton";
import Chip from "@mui/joy/Chip";
import ChipDelete from "@mui/joy/ChipDelete";
import Tooltip from "@mui/joy/Tooltip";
import Typography from "@mui/joy/Typography";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import DragIndicatorRoundedIcon from "@mui/icons-material/DragIndicatorRounded";
import KeyRoundedIcon from "@mui/icons-material/KeyRounded";
import { ColumnTypeSelector } from "./ColumnTypeSelector";
import type { SchemaBuilderColumn, SimpleColumnType } from "../../types";

interface ColumnEditorProps {
  column: SchemaBuilderColumn;
  onUpdate: (updates: Partial<SchemaBuilderColumn>) => void;
  onDelete: () => void;
  isDeleting?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dragHandleProps?: any;
  isDragging?: boolean;
}

export function ColumnEditor({
  column,
  onUpdate,
  onDelete,
  isDeleting = false,
  dragHandleProps,
  isDragging,
}: ColumnEditorProps) {
  const [selectOptions, setSelectOptions] = useState<string[]>(
    column.selectOptions || [],
  );
  const [newOption, setNewOption] = useState("");

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const displayName = e.target.value;
    // Auto-generate snake_case name from display name
    const name = displayName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_|_$/g, "");
    onUpdate({ displayName, name });
  };

  const handleTypeChange = (dataType: SimpleColumnType) => {
    onUpdate({ dataType });
    // Clear select options if changing from Selection type
    if (dataType !== "Selection") {
      setSelectOptions([]);
      onUpdate({ dataType, selectOptions: undefined });
    }
  };

  const handleAddOption = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && newOption.trim()) {
      const updated = [...selectOptions, newOption.trim()];
      setSelectOptions(updated);
      onUpdate({ selectOptions: updated });
      setNewOption("");
    }
  };

  const handleRemoveOption = (index: number) => {
    const updated = selectOptions.filter((_, i) => i !== index);
    setSelectOptions(updated);
    onUpdate({ selectOptions: updated });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        p: 1.5,
        borderRadius: "sm",
        bgcolor: isDragging ? "background.level2" : "background.surface",
        border: "1px solid",
        borderColor: isDragging ? "primary.300" : "divider",
        transition: "border-color 0.2s, background-color 0.2s",
        "&:hover": {
          borderColor: "neutral.400",
        },
      }}
    >
      {/* Main row */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {/* Drag handle */}
        <Box
          {...dragHandleProps}
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "grab",
            color: "text.tertiary",
            "&:hover": { color: "text.secondary" },
          }}
        >
          <DragIndicatorRoundedIcon fontSize="small" />
        </Box>

        {/* Primary key indicator */}
        {column.isPrimaryKey && (
          <Tooltip title="Primary Key (ID)" placement="top">
            <KeyRoundedIcon fontSize="small" sx={{ color: "warning.500" }} />
          </Tooltip>
        )}

        {/* Column name */}
        <Input
          size="sm"
          placeholder="Column name"
          value={column.displayName}
          onChange={handleNameChange}
          disabled={column.isPrimaryKey}
          sx={{ flex: 1, minWidth: 120 }}
        />

        {/* Type selector */}
        <ColumnTypeSelector
          value={column.dataType}
          onChange={handleTypeChange}
          disabled={column.isPrimaryKey}
        />

        {/* Required checkbox */}
        <Tooltip title="Required field" placement="top">
          <Checkbox
            size="sm"
            label="Required"
            checked={column.isRequired}
            onChange={(e) => onUpdate({ isRequired: e.target.checked })}
            disabled={column.isPrimaryKey}
            sx={{ ml: 1 }}
          />
        </Tooltip>

        {/* Delete button */}
        {!column.isPrimaryKey && (
          <IconButton
            size="sm"
            variant="plain"
            color="danger"
            onClick={onDelete}
            disabled={isDeleting}
            sx={{ ml: "auto" }}
          >
            <DeleteRoundedIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Selection options (when type is Selection) */}
      {column.dataType === "Selection" && (
        <Box sx={{ pl: 4, display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
            Options (press Enter to add):
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {selectOptions.map((option, index) => (
              <Chip
                key={index}
                size="sm"
                variant="soft"
                endDecorator={
                  <ChipDelete onDelete={() => handleRemoveOption(index)} />
                }
              >
                {option}
              </Chip>
            ))}
            <Input
              size="sm"
              placeholder="Add option..."
              value={newOption}
              onChange={(e) => setNewOption(e.target.value)}
              onKeyDown={handleAddOption}
              sx={{ width: 120 }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default ColumnEditor;
